/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { accessSetResourceAudience } from "../funcs/accessSetResourceAudience.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { ResourceAudienceResult } from "../models/components/resourceaudienceresult.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  SetResourceAudienceRequest,
  SetResourceAudienceSecurity,
} from "../models/operations/setresourceaudience.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type SetResourceAudienceMutationVariables = {
  request: SetResourceAudienceRequest;
  security?: SetResourceAudienceSecurity | undefined;
  options?: RequestOptions;
};

export type SetResourceAudienceMutationData = ResourceAudienceResult;

export type SetResourceAudienceMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * setResourceAudience access
 *
 * @remarks
 * Replace the rules that name one resource. Organization-wide rules are left untouched.
 */
export function useSetResourceAudienceMutation(
  options?: MutationHookOptions<
    SetResourceAudienceMutationData,
    SetResourceAudienceMutationError,
    SetResourceAudienceMutationVariables
  >,
): UseMutationResult<
  SetResourceAudienceMutationData,
  SetResourceAudienceMutationError,
  SetResourceAudienceMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildSetResourceAudienceMutation(client, options),
    ...options,
  });
}

export function mutationKeySetResourceAudience(): MutationKey {
  return ["@gram/client", "access", "setResourceAudience"];
}

export function buildSetResourceAudienceMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: SetResourceAudienceMutationVariables,
  ) => Promise<SetResourceAudienceMutationData>;
} {
  return {
    mutationKey: mutationKeySetResourceAudience(),
    mutationFn: function setResourceAudienceMutationFn({
      request,
      security,
      options,
    }): Promise<SetResourceAudienceMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(accessSetResourceAudience(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
